import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CalendarDays, CalendarCheck } from "lucide-react";
import { api, apiError, fmtDate, MONTHS } from "@/lib/api";

const WEEKDAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const today = new Date().toISOString().slice(0, 10);
const nowD = new Date();

const fmtDur = (start, end) => {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const mins = Math.max(0, eh * 60 + em - (sh * 60 + sm));
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
};

export default function AdminSchedules() {
  const [operators, setOperators] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [form, setForm] = useState({ operator_id: "", weekday: 0, start_time: "09:00", end_time: "13:00" });
  const [asnForm, setAsnForm] = useState({ operator_id: "", date: today, start_time: "09:00", end_time: "13:00", note: "" });
  const [month, setMonth] = useState(nowD.getMonth() + 1);
  const year = nowD.getFullYear();

  const load = useCallback(async () => {
    try {
      const [o, s, a] = await Promise.all([
        api.get("/operators"),
        api.get("/schedules"),
        api.get("/shift-assignments", { params: { year, month } }),
      ]);
      setOperators(o.data.filter((x) => x.role === "operator"));
      setSchedules(s.data);
      setAssignments(a.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.operator_id) return toast.error("Seleziona un operatore");
    try {
      await api.post("/schedules", { ...form, weekday: Number(form.weekday) });
      toast.success("Turno ricorrente aggiunto");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/schedules/${id}`);
      toast.success("Turno eliminato");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addAssignment = async (e) => {
    e.preventDefault();
    if (!asnForm.operator_id) return toast.error("Seleziona un operatore");
    try {
      await api.post("/shift-assignments", asnForm);
      toast.success("Turno assegnato");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeAssignment = async (id) => {
    try {
      await api.delete(`/shift-assignments/${id}`);
      toast.success("Turno eliminato");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const nameOf = (oid) => operators.find((o) => o.id === oid)?.name || "—";

  const grouped = WEEKDAYS.map((day, i) => ({
    day,
    weekday: i,
    items: schedules.filter((s) => s.weekday === i).sort((a, b) => a.start_time.localeCompare(b.start_time)),
  }));

  const totalMinsForOp = (oid) => {
    let total = 0;
    for (const s of schedules) {
      if (s.operator_id !== oid) continue;
      const [sh, sm] = s.start_time.split(":").map(Number);
      const [eh, em] = s.end_time.split(":").map(Number);
      total += Math.max(0, eh * 60 + em - (sh * 60 + sm));
    }
    return total;
  };

  return (
    <div className="space-y-6" data-testid="admin-schedules">
      <div>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold">Turni Programmati</h1>
        <p className="text-sm text-gray-600 mt-1">Pianifica gli orari settimanali del team. Le ore previste vengono confrontate automaticamente con quelle effettive.</p>
      </div>

      <form onSubmit={add} className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-600">Operatore</label>
          <select
            data-testid="schedule-operator"
            required
            value={form.operator_id}
            onChange={(e) => setForm({ ...form, operator_id: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm"
          >
            <option value="">Seleziona</option>
            {operators.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-600">Giorno</label>
          <select
            data-testid="schedule-weekday"
            value={form.weekday}
            onChange={(e) => setForm({ ...form, weekday: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm"
          >
            {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-600">Ingresso</label>
          <input
            data-testid="schedule-start"
            type="time"
            value={form.start_time}
            onChange={(e) => setForm({ ...form, start_time: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm font-mono-num"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-600">Uscita</label>
          <input
            data-testid="schedule-end"
            type="time"
            value={form.end_time}
            onChange={(e) => setForm({ ...form, end_time: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm font-mono-num"
          />
        </div>
        <button
          data-testid="schedule-add-btn"
          className="h-11 px-5 rounded-lg bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors hover:bg-emerald-700 active:scale-95"
        >
          <Plus className="h-4 w-4" /> Aggiungi
        </button>
      </form>

      {operators.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-emerald-600" />
            <h2 className="text-base font-semibold">Ore settimanali per operatore</h2>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {operators.map((o) => {
              const mins = totalMinsForOp(o.id);
              return (
                <div key={o.id} data-testid={`weekly-total-${o.id}`} className="rounded-lg border border-gray-100 p-4">
                  <p className="text-xs text-gray-500 truncate">{o.name}</p>
                  <p className="font-mono-num font-bold text-lg mt-1">{Math.floor(mins / 60)}h {String(mins % 60).padStart(2, "0")}m</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {grouped.map(({ day, weekday, items }) => (
          <div key={weekday} data-testid={`day-column-${weekday}`} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
            <h3 className="font-semibold text-sm text-emerald-700">{day}</h3>
            {items.length === 0 ? (
              <p className="text-xs text-gray-400 mt-3">Nessun turno</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {items.map((s) => (
                  <li key={s.id} data-testid={`schedule-row-${s.id}`} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{nameOf(s.operator_id)}</p>
                      <p className="text-xs text-gray-500 font-mono-num">{s.start_time} – {s.end_time} ({fmtDur(s.start_time, s.end_time)})</p>
                    </div>
                    <button
                      data-testid={`schedule-delete-${s.id}`}
                      onClick={() => remove(s.id)}
                      aria-label="Elimina turno"
                      className="h-8 w-8 rounded-lg border border-red-200 text-red-600 flex items-center justify-center transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Turni per data specifica */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mt-8" data-testid="date-shifts-panel">
        <div className="flex items-center gap-2 mb-1">
          <CalendarCheck className="h-4 w-4 text-emerald-600" />
          <h2 className="text-base font-semibold">Turni per data specifica</h2>
        </div>
        <p className="text-xs text-gray-500 mb-5">Utile per coperture straordinarie, sostituzioni o eventi. Non sostituiscono i turni ricorrenti settimanali sopra.</p>

        <form onSubmit={addAssignment} className="grid grid-cols-1 sm:grid-cols-6 gap-3 items-end mb-6">
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-wider text-gray-600">Operatore</label>
            <select
              data-testid="assignment-operator"
              required
              value={asnForm.operator_id}
              onChange={(e) => setAsnForm({ ...asnForm, operator_id: e.target.value })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm"
            >
              <option value="">Seleziona</option>
              {operators.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">Data</label>
            <input
              data-testid="assignment-date"
              type="date"
              value={asnForm.date}
              onChange={(e) => setAsnForm({ ...asnForm, date: e.target.value })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">Ingresso</label>
            <input
              data-testid="assignment-start"
              type="time"
              value={asnForm.start_time}
              onChange={(e) => setAsnForm({ ...asnForm, start_time: e.target.value })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-2 text-sm font-mono-num"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">Uscita</label>
            <input
              data-testid="assignment-end"
              type="time"
              value={asnForm.end_time}
              onChange={(e) => setAsnForm({ ...asnForm, end_time: e.target.value })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-2 text-sm font-mono-num"
            />
          </div>
          <button
            data-testid="assignment-add-btn"
            className="h-11 px-4 rounded-lg bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors hover:bg-emerald-700 active:scale-95"
          >
            <Plus className="h-4 w-4" /> Aggiungi
          </button>
        </form>

        <div className="flex items-center gap-3 mb-4">
          <select
            data-testid="assignment-month"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm"
          >
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m} {year}</option>)}
          </select>
          <span className="text-xs text-gray-500">{assignments.length} turni assegnati</span>
        </div>

        {assignments.length === 0 ? (
          <p data-testid="assignments-empty" className="text-sm text-gray-500">Nessun turno assegnato per il mese selezionato.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-600">
                <th className="pb-3">Data</th><th className="pb-3">Operatore</th><th className="pb-3">Ingresso</th><th className="pb-3">Uscita</th><th className="pb-3">Durata</th><th className="pb-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id} data-testid={`assignment-row-${a.id}`} className="border-t border-gray-100">
                  <td className="py-3 font-mono-num">{fmtDate(a.date)}</td>
                  <td className="py-3 font-medium">{a.operator_name}</td>
                  <td className="py-3 font-mono-num">{a.start_time}</td>
                  <td className="py-3 font-mono-num">{a.end_time}</td>
                  <td className="py-3 font-mono-num text-gray-600">{fmtDur(a.start_time, a.end_time)}</td>
                  <td className="py-3 text-right">
                    <button
                      data-testid={`assignment-delete-${a.id}`}
                      onClick={() => removeAssignment(a.id)}
                      aria-label="Elimina turno assegnato"
                      className="h-8 w-8 rounded-lg border border-red-200 text-red-600 inline-flex items-center justify-center hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
